<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { AssignmentStatus, TeamStatus } from '@/types'
import type { Assignment, Team } from '@/types'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const props = defineProps<{
  assignment: Assignment
  assignedTeams: Team[]
  teams: Team[]
  canEdit: boolean
  permissionRole?: string | null
  caltopoMapId?: string | null
}>()

type PlatformFields = Partial<Pick<Assignment,
  'assignment_name' | 'time_allocated' |
  'description' | 'resource_type' | 'team_size' | 'priority' |
  'responsive_pod' | 'unresponsive_pod' | 'clue_pod' | 'transportation' |
  'primary_frequency' | 'secondary_frequency'
>>

const emit = defineEmits<{
  close: []
  assignTeam: [teamId: string]
  detachTeam: [teamId: string]
  statusChange: [status: AssignmentStatus]
  save: [fields: PlatformFields]
}>()

// ── Editable platform fields ───────────────────────────────────────────────

const draft = ref({
  assignment_name: props.assignment.assignment_name ?? '',
  // CalTopo bidirectional fields
  time_allocated: props.assignment.time_allocated ?? '',
  description: props.assignment.description ?? '',
  resource_type: props.assignment.resource_type ?? null,
  team_size: props.assignment.team_size ?? '',
  priority: props.assignment.priority ?? null,
  responsive_pod: props.assignment.responsive_pod ?? null,
  unresponsive_pod: props.assignment.unresponsive_pod ?? null,
  clue_pod: props.assignment.clue_pod ?? null,
  transportation: props.assignment.transportation ?? '',
  primary_frequency: props.assignment.primary_frequency ?? '',
  secondary_frequency: props.assignment.secondary_frequency ?? '',
  previous_efforts: props.assignment.previous_efforts ?? '',
})

// Keep draft in sync if a different assignment is passed in (shouldn't normally happen)
watch(() => props.assignment.assignment_id, () => {
  draft.value = {
    assignment_name: props.assignment.assignment_name ?? '',
    time_allocated: props.assignment.time_allocated ?? '',
    description: props.assignment.description ?? '',
    resource_type: props.assignment.resource_type ?? null,
    team_size: props.assignment.team_size ?? '',
    priority: props.assignment.priority ?? null,
    responsive_pod: props.assignment.responsive_pod ?? null,
    unresponsive_pod: props.assignment.unresponsive_pod ?? null,
    clue_pod: props.assignment.clue_pod ?? null,
    transportation: props.assignment.transportation ?? '',
    primary_frequency: props.assignment.primary_frequency ?? '',
    secondary_frequency: props.assignment.secondary_frequency ?? '',
    previous_efforts: props.assignment.previous_efforts ?? '',
  }
})

function onBlur(field: keyof typeof draft.value) {
  const val = draft.value[field]
  const cur = (props.assignment as unknown as Record<string, unknown>)[field]
  if (val !== cur) {
    emit('save', { [field]: val } as PlatformFields)
  }
}

// ── Team assignment ────────────────────────────────────────────────────────

const selectedTeamId = ref<string | null>(null)

const assignedTeamIds = computed(() => new Set(props.assignedTeams.map((t) => t.team_id)))

const teamOptions = computed(() =>
  props.teams
    .filter((t) => t.team_status === TeamStatus.AwaitingAssignment && !assignedTeamIds.value.has(t.team_id))
    .map((t) => ({ label: t.team_identifier, value: t.team_id })),
)

function onAssign() {
  if (!selectedTeamId.value) return
  emit('assignTeam', selectedTeamId.value)
  selectedTeamId.value = null
}

// ── Status badge + transitions ─────────────────────────────────────────────

const statusClass = computed(() => {
  switch (props.assignment.assignment_status) {
    case AssignmentStatus.Draft:       return 'bg-amber-50 text-amber-700'
    case AssignmentStatus.NotStarted:  return 'bg-surface-container-highest text-on-surface-variant'
    case AssignmentStatus.InProgress:  return 'bg-primary-container/20 text-primary'
    case AssignmentStatus.Completed:   return 'bg-surface-container-highest text-on-surface'
    default:                           return 'bg-surface-container-highest text-on-surface-variant'
  }
})

const statusOptions = [
  { label: 'Draft', value: AssignmentStatus.Draft },
  { label: 'Not Started', value: AssignmentStatus.NotStarted },
  { label: 'In Progress', value: AssignmentStatus.InProgress },
  { label: 'Completed', value: AssignmentStatus.Completed },
]

const caltopoResourceOptions = [
  { label: 'Ground', value: 'GROUND' },
  { label: 'Air', value: 'AIR' },
  { label: 'Water', value: 'WATER' },
  { label: 'K9', value: 'K9' },
]

const caltopoPodOptions = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
]

const caltopoPriorityOptions = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
]
</script>

<template>
  <FosDialog :open="true" max-width="max-w-3xl" @update:open="emit('close')">
    <template #title>
      <div class="flex items-center justify-between gap-3 w-full pr-2">
        <FosInput
          v-model="draft.assignment_name"
          :disabled="!canEdit"
          placeholder="Assignment name"
          class="flex-1 font-semibold"
          @blur="onBlur('assignment_name')"
        />
        <PermissionGuard action="assignment.create" :role="permissionRole">
          <FosSelect
            :model-value="assignment.assignment_status"
            :options="statusOptions"
            :trigger-class="`inline-flex items-center px-2.5 py-0.5 text-xs font-mono font-medium cursor-pointer ${statusClass}`"
            icon-class="material-symbols-outlined text-current text-[12px] ml-0.5 shrink-0 opacity-60"
            @update:model-value="emit('statusChange', $event as AssignmentStatus)"
          />
        </PermissionGuard>
      </div>
    </template>

    <!-- ── Description (full-width, always shown) ── -->
    <div class="pt-1">
      <FosTextarea
        v-model="draft.description"
        :rows="2"
        :disabled="!canEdit"
        :auto-resize="true"
        placeholder="Description…"
        @blur="onBlur('description')"
      />
    </div>

    <!-- ── Merged details section ── -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest">Details</p>
        <a
          v-if="assignment.caltopo_assignment_id && caltopoMapId"
          :href="`https://caltopo.com/m/${caltopoMapId}`"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center gap-1 text-[11px] font-mono text-primary hover:underline"
        >
          <span class="material-symbols-outlined text-[12px]">open_in_new</span>
          View on CalTopo
        </a>
      </div>

      <!-- CalTopo read-only metadata -->
      <!-- <div
        v-if="assignment.caltopo_assignment_id && (assignment.letter || assignment.number || assignment.prepared_by || assignment.previous_efforts)"
        class="grid grid-cols-3 gap-x-3 gap-y-1 mb-3 bg-surface-container-low border border-border p-2"
      >
        <div v-if="assignment.letter">
          <p class="text-[10px] text-outline uppercase tracking-widest mb-0.5">Letter</p>
          <p class="text-sm font-mono text-on-surface">{{ assignment.letter }}</p>
        </div>
        <div v-if="assignment.number">
          <p class="text-[10px] text-outline uppercase tracking-widest mb-0.5">Team #</p>
          <p class="text-sm font-mono text-on-surface">{{ assignment.number }}</p>
        </div>
        <div v-if="assignment.prepared_by">
          <p class="text-[10px] text-outline uppercase tracking-widest mb-0.5">Prepared By</p>
          <p class="text-sm text-on-surface truncate">{{ assignment.prepared_by }}</p>
        </div>
        <div v-if="assignment.previous_efforts" class="col-span-3">
          <p class="text-[10px] text-outline uppercase tracking-widest mb-0.5">Previous Efforts</p>
          <p class="text-sm text-on-surface">{{ assignment.previous_efforts }}</p>
        </div>
      </div> -->

      <!-- Editable fields -->
      <div class="grid grid-cols-3 gap-x-4 gap-y-3">
        <!-- CalTopo bidirectional fields -->
        <template v-if="assignment.caltopo_assignment_id">
          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Resource Type</label>
            <FosSelect
              :model-value="draft.resource_type ?? ''"
              :options="caltopoResourceOptions"
              placeholder="Select…"
              :disabled="!canEdit"
              @update:model-value="draft.resource_type = ($event || null) as typeof draft.resource_type; emit('save', { resource_type: draft.resource_type })"
            />
          </div>

          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Priority</label>
            <FosSelect
              :model-value="draft.priority ?? ''"
              :options="caltopoPriorityOptions"
              placeholder="Select…"
              :disabled="!canEdit"
              @update:model-value="draft.priority = ($event || null) as typeof draft.priority; emit('save', { priority: draft.priority })"
            />
          </div>

          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Transportation</label>
            <FosInput
              v-model="draft.transportation"
              :disabled="!canEdit"
              placeholder="e.g. POV"
              @blur="emit('save', { transportation: draft.transportation || null })"
            />
          </div>
        </template>

        <!-- Always-shown fields -->
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Time Allocated (hrs)</label>
          <FosInput
            v-model="draft.time_allocated"
            :disabled="!canEdit"
            placeholder="e.g. 4"
            @blur="onBlur('time_allocated')"
          />
        </div>

        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Team Size</label>
          <FosInput
            v-model="draft.team_size"
            :disabled="!canEdit"
            placeholder="e.g. 3"
            @blur="onBlur('team_size')"
          />
        </div>

        <!-- CalTopo POD fields — grouped -->
        <div v-if="assignment.caltopo_assignment_id" class="col-span-3 grid grid-cols-3 gap-x-3 gap-y-3 bg-surface-container-low border border-border p-3">
          <p class="col-span-3 text-[10px] text-outline font-label uppercase tracking-widest -mb-1">Probability of Detection</p>
          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Responsive</label>
            <FosSelect
              :model-value="draft.responsive_pod ?? ''"
              :options="caltopoPodOptions"
              placeholder="—"
              :disabled="!canEdit"
              @update:model-value="draft.responsive_pod = ($event || null) as typeof draft.responsive_pod; emit('save', { responsive_pod: draft.responsive_pod })"
            />
          </div>

          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Unresponsive</label>
            <FosSelect
              :model-value="draft.unresponsive_pod ?? ''"
              :options="caltopoPodOptions"
              placeholder="—"
              :disabled="!canEdit"
              @update:model-value="draft.unresponsive_pod = ($event || null) as typeof draft.unresponsive_pod; emit('save', { unresponsive_pod: draft.unresponsive_pod })"
            />
          </div>

          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Clue</label>
            <FosSelect
              :model-value="draft.clue_pod ?? ''"
              :options="caltopoPodOptions"
              placeholder="—"
              :disabled="!canEdit"
              @update:model-value="draft.clue_pod = ($event || null) as typeof draft.clue_pod; emit('save', { clue_pod: draft.clue_pod })"
            />
          </div>
        </div>

        <!-- CalTopo frequency fields — grouped -->
        <div v-if="assignment.caltopo_assignment_id" class="col-span-3 grid grid-cols-2 gap-x-4 gap-y-3 bg-surface-container-low border border-border p-3">
          <p class="col-span-2 text-[10px] text-outline font-label uppercase tracking-widest -mb-1">Radio Frequencies</p>
          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Primary</label>
            <FosInput
              v-model="draft.primary_frequency"
              :disabled="!canEdit"
              placeholder="e.g. ICS 3"
              @blur="emit('save', { primary_frequency: draft.primary_frequency || null })"
            />
          </div>

          <div>
            <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Secondary</label>
            <FosInput
              v-model="draft.secondary_frequency"
              :disabled="!canEdit"
              placeholder="e.g. SAR 1"
              @blur="emit('save', { secondary_frequency: draft.secondary_frequency || null })"
            />
          </div>
        </div>

        <div class="col-span-3">
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">Previous Efforts</label>
          <FosTextarea
            v-model="draft.previous_efforts"
            :disabled="!canEdit"
            :rows="3"
            :auto-resize="true"
            placeholder="Previous efforts, if any"
            @blur="onBlur('previous_efforts')"
          />
        </div>
      </div>
    </div>

    <!-- ── Assigned Teams (bottom) ── -->
    <div>
      <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-2 mt-2">Assigned Teams</p>
      <div class="bg-surface-container-low p-3 flex flex-col gap-2">
        <!-- Assigned team rows -->
        <div v-if="assignedTeams.length > 0" class="flex flex-col gap-1.5">
          <div
            v-for="team in assignedTeams"
            :key="team.team_id"
            class="flex items-center justify-between"
          >
            <span class="inline-flex items-center gap-1.5 bg-surface-container-highest border border-border px-2.5 py-1 text-xs font-mono text-on-surface-variant">
              <span class="material-symbols-outlined text-[12px]">group</span>
              {{ team.team_identifier }}
            </span>
            <PermissionGuard v-if="assignment.assignment_status !== AssignmentStatus.Completed" action="assignment.create" :role="permissionRole">
              <button
                class="text-outline hover:text-error transition-colors p-1"
                title="Remove team"
                @click="emit('detachTeam', team.team_id)"
              >
                <span class="material-symbols-outlined text-[16px]">close</span>
              </button>
            </PermissionGuard>
          </div>
        </div>

        <!-- Empty state -->
        <p v-else class="text-xs text-outline font-mono italic">
          {{ assignment.assignment_status === AssignmentStatus.Completed ? 'Assignment completed' : 'No team assigned' }}
        </p>

        <!-- Add team row -->
        <div
          v-if="assignment.assignment_status !== AssignmentStatus.Completed && assignedTeams.length < 2"
          class="flex items-center gap-2 pt-2 border-t border-border mt-1"
        >
          <PermissionGuard action="assignment.create" :role="permissionRole">
            <FosSelect
              :model-value="selectedTeamId ?? ''"
              :options="teamOptions"
              placeholder="Add a team…"
              @update:model-value="selectedTeamId = $event || null"
            />
            <FosButton
              variant="secondary"
              size="sm"
              :disabled="!selectedTeamId"
              @click="onAssign"
            >
              Assign
            </FosButton>
          </PermissionGuard>
        </div>
      </div>
    </div>

  </FosDialog>
</template>
